import type { FoodRepository } from "../ports/repositories";
import type { FoodSource } from "../ports/food-source";
import {
  createImportedFoodDraft,
  type ExternalFoodCandidate,
  type ImportedFoodDraft,
} from "../../domain/foods/import-draft";
import type { Food } from "../../domain/foods/food";

export interface FoodSearchResponse {
  readonly local: readonly Food[];
  readonly online: readonly { readonly draft: ImportedFoodDraft; readonly attribution?: string }[];
}

export async function searchFoods(
  dependencies: { readonly foods: FoodRepository; readonly sources: readonly FoodSource[] },
  input: { readonly query: string; readonly limit?: number; readonly includeOnline?: boolean },
): Promise<FoodSearchResponse> {
  const query = input.query.trim();
  if (query.length < 2) throw new Error("Food search requires at least two characters");
  const limit = Math.max(1, Math.min(50, Math.floor(input.limit ?? 20)));

  if (/^\d{6,14}$/.test(query)) {
    const localBarcodeMatch = await dependencies.foods.findByBarcode(query);
    if (localBarcodeMatch && !needsProviderMeasureRefresh(localBarcodeMatch)) {
      return { local: [localBarcodeMatch], online: [] };
    }
    if (input.includeOnline === false) {
      return { local: localBarcodeMatch ? [localBarcodeMatch] : [], online: [] };
    }

    for (const source of dependencies.sources) {
      try {
        const result = await source.findByBarcode(query);
        if (result && (!localBarcodeMatch || isProviderMeasureRefresh(localBarcodeMatch, result.candidate))) {
          return {
            local: localBarcodeMatch ? [localBarcodeMatch] : [],
            online: [{
              draft: createImportedFoodDraft(result.candidate),
              attribution: result.attribution,
            }],
          };
        }
      } catch (error) {
        if (!localBarcodeMatch) throw error;
      }
    }
    return { local: localBarcodeMatch ? [localBarcodeMatch] : [], online: [] };
  }

  const local = await dependencies.foods.search(query, limit);
  if (input.includeOnline === false) return { local, online: [] };

  const sourceResults = await Promise.all(dependencies.sources.map((source) => source.search(query, limit)));
  return {
    local,
    online: sourceResults.flat().flatMap((result) => {
      try {
        return [{
          draft: createImportedFoodDraft(result.candidate),
          attribution: result.attribution,
        }];
      } catch {
        // Public datasets can contain isolated invalid records. Preserve every
        // usable search result instead of failing the entire search response.
        return [];
      }
    }),
  };
}

function needsProviderMeasureRefresh(
  food: Food,
): food is Food & { readonly provenance: Extract<Food["provenance"], { readonly kind: "imported" }> } {
  return food.provenance.kind === "imported" && (food.measures?.length ?? 0) === 0;
}

function isProviderMeasureRefresh(food: Food, candidate: ExternalFoodCandidate): boolean {
  return needsProviderMeasureRefresh(food)
    && (candidate.measures?.length ?? 0) > 0
    && food.provenance.provider === candidate.provider
    && food.provenance.providerFoodId === candidate.providerFoodId;
}
